import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { finalize, map, Observable} from "rxjs";
import {
  ActionResult,
  ActionResultList, AssignChipNumbersResponse,
  Competitor,
  CompetitorFilter,
  ManageCompetitorErrorCodes,
  ManageCompetitorRequest,
  PagedFilter, RowError, AssignChipNumbersRequest
} from "@model";
import {LoaderService} from "./loader.service";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn: 'root' })
export class CompetitorService {

  public static INVALID_CHIP_NUMBER = 2;

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService,
  ) {}

  manageCompetitor(request: Competitor, onSuccess?: (result: Competitor) => void, onError?: (errorCode?: number) => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<Competitor>>(`${this.environmentService.getApiUrl()}/api/competitors/manage`, request)
      .subscribe(actionResult => {
        this.loaderService.dismissLoading();
        actionResult.result ? onSuccess?.(actionResult.result) : onError?.(actionResult.resultCode);
      });
  }

  manageCompetitorTimingData(request: Competitor, onSuccess?: () => void, onError?: (errorCode?: ManageTimingDataResponse) => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<ManageTimingDataResponse>>(`${this.environmentService.getApiUrl()}/api/competitors/manage/timingData`, request)
      .subscribe(actionResult => {
        const result = actionResult.result;
        this.loaderService.dismissLoading();
        result?.success ? onSuccess?.() : onError?.(result);
      });
  }

  manageCompetitorStatus(competitor: Competitor, onSuccess?: () => void, onError?: (errorCode?: number) => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/competitors/status/manage`, competitor)
      .pipe(finalize(() => this.loaderService.dismissLoading()))
      .subscribe(actionResult => {
        actionResult?.result ? onSuccess?.() : onError?.(actionResult?.resultCode);
      });
  }

  manageCompetitorRace(competitor: Competitor, onSuccess?: (competitor: Competitor)=> void, onError?: (errorCode?: ManageCompetitorErrorCodes) => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<Competitor>>(`${this.environmentService.getApiUrl()}/api/competitors/change/race`, competitor)
        .pipe(finalize(() => this.loaderService.dismissLoading()))
        .subscribe(actionResult => {
          actionResult?.result ? onSuccess?.(actionResult.result) : onError?.(actionResult?.resultCode);
        });
  }

  findCompetitors(filter: PagedFilter<CompetitorFilter>, isPrivate: boolean = false, isMeasurement: boolean = false): Observable<Competitor[]> {
    if (isPrivate && isMeasurement) {
      filter.filter.isMeasurement = true;
    }

    return this.http.post<ActionResultList<Competitor[], number>>(this.environmentService.getApiUrl() + (isPrivate ? "/api/competitors/find" : "/api/public/competitors/find"), filter)
      .pipe(map(value => {
        return value?.result?.length ? value.result : []
      }));
  }

  fetchCompetitorById(competitorId: number): Observable<Competitor | undefined> {
    return this.http.get<ActionResult<Competitor>>(`${this.environmentService.getApiUrl()}/api/competitors/fetchbyid/${competitorId}`)
      .pipe(map(value => value?.result));
  }


  fetchCompetitor(startNumber: string, raceId?: number, eventId?:number, waypointId?:number, roundRace?:boolean, extension?: string,): Observable<Competitor | undefined> {
    let url = `${this.environmentService.getApiUrl()}/api/competitors/fetch?startNumber=${startNumber}&eventId=${eventId}`;

    if (extension?.length) {
      url += `&extension=${extension}`
    }
    if (waypointId !== null && waypointId !== undefined) {
      url += `&waypointId=${waypointId}`;
    }
    if (raceId) {
      url += `&raceId=${raceId}`;
    }
    if (roundRace !== undefined) {
      url += `&roundRace=${roundRace}`;
    }
    return this.http.get<ActionResult<Competitor>>(url).pipe(map(value => value?.result));
  }


  assignChipNumbers(eventId: number|undefined, assignChipNumbersRequest: AssignChipNumbersRequest, onSuccess?: (response: AssignChipNumbersResponse)=> void, onError?: (errorCode?: number | undefined) => void) {
     this.http.post<ActionResult<AssignChipNumbersResponse>>(
         `${this.environmentService.getApiUrl()}/api/competitors/assignChipNumbers${eventId ? `?eventId=${eventId}` : ''}`,
         assignChipNumbersRequest)
         .pipe(finalize(() => this.loaderService.dismissLoading()))
         .subscribe(actionResult => {
          actionResult?.result ? onSuccess?.(actionResult.result) : onError?.(actionResult?.resultCode);
        });
  }

  deleteCompetitor(competitorId: number): Observable<boolean> {
    return this.http.delete<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/competitors/delete?competitorId=${competitorId}`).pipe(
      map(value => value.result ?? false)
    );
  }

  importFromExcel(raceId: number, file: File): Observable<ActionResult<RowError[]>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.request<ActionResult<RowError[]>>('POST',
      `${this.environmentService.getApiUrl()}/api/competitors/import/${raceId}`,
      { body: formData }
    );
  }
}

export class ManageTimingDataResponse {
  startNumberError?: ManageCompetitorErrorCodes;
  duplicateChipNumbers?: string[];
  success?: boolean;
}
